import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { isUnavailable } from "../useLearning";
import { FILTER_DEBOUNCE_MS } from "./catalogText";

export const STATUS = Object.freeze({ LOADING: "loading", READY: "ready", NOT_CONNECTED: "not_connected", FAILED: "failed" });

const ACTION = Object.freeze({
  SET: "set", COMMIT_TEXT: "commit_text", CLEAR: "clear", OFFSET: "offset", LIMIT: "limit", REFRESH: "refresh",
});
const TEXT_SEPARATOR = "\u0000";
const NO_RESETS = Object.freeze({});

const lastPageOffset = (total, limit) => Math.floor((total - 1) / limit) * limit;

/**
 * `filters` is what the filter bar shows; `query` is what the server is asked for. A text filter only reaches `query` once the
 * typing settles (COMMIT_TEXT); any other filter reaches it at once, together with whatever text is already typed. Every
 * filter change goes back to the first page. A new `query` object is the one signal that a page must be fetched.
 */
function reducer(state, action) {
  const { filters, query } = state;
  switch (action.type) {
    case ACTION.SET: {
      const next = { ...filters, [action.key]: action.value, ...(action.resets[action.key] || NO_RESETS) };
      if (action.textKeys.includes(action.key)) return { filters: next, query };
      return { filters: next, query: { ...query, filters: next, offset: 0 } };
    }
    case ACTION.COMMIT_TEXT: {
      const { settled } = action;
      if (Object.keys(settled).every((k) => settled[k] === query.filters[k])) return state;
      return { filters, query: { ...query, filters: { ...query.filters, ...settled }, offset: 0 } };
    }
    case ACTION.CLEAR:
      return { filters: action.empty, query: { ...query, filters: action.empty, offset: 0 } };
    case ACTION.OFFSET:
      return query.offset === action.offset ? state : { filters, query: { ...query, offset: action.offset } };
    case ACTION.LIMIT:
      return query.limit === action.limit ? state : { filters, query: { ...query, limit: action.limit, offset: 0 } };
    case ACTION.REFRESH:
      return { filters, query: { ...query, round: query.round + 1 } };
    default:
      throw new Error(`Unknown paged-list action: ${action.type}`);
  }
}

const initialState = ({ empty, limit }) => ({ filters: empty, query: { filters: empty, offset: 0, limit, round: 0 } });

/**
 * One server-side page of a filtered list: exactly one request per settled query (`load({...filters, limit, offset}, signal)`),
 * newest first as the server orders it; nothing is sorted, sliced or filtered here. A superseded request is aborted. While the
 * next page loads the previous one stays (status LOADING, `data` kept). Nothing loads while `enabled` is false, and coming back
 * to an unchanged, already-loaded query does not fetch again; `refresh()` while disabled makes the next show reload.
 * A page past the end (the last page emptied) moves to the new last page. A missing endpoint (null) or an unreachable store
 * (503) is "not connected", not an error.
 *
 * Returns {status, data, error, filters, offset, limit, setFilter, clearFilters, setOffset, setLimit, refresh}.
 */
export default function usePagedList({ enabled, empty, textKeys, resets = NO_RESETS, limit: initialLimit, load }) {
  const [{ filters, query }, dispatch] = useReducer(reducer, { empty, limit: initialLimit }, initialState);
  const [page, setPage] = useState({ status: STATUS.LOADING, data: null, error: "" });
  const loaded = useRef(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  // The text commits FILTER_DEBOUNCE_MS after the last keystroke, whatever it settled on (typing back to the committed text
  // after another filter flushed a newer one must still commit).
  const typed = textKeys.map((k) => filters[k]).join(TEXT_SEPARATOR);
  useEffect(() => {
    const values = typed.split(TEXT_SEPARATOR);
    const settled = Object.fromEntries(textKeys.map((k, i) => [k, values[i]]));
    const timer = setTimeout(() => dispatch({ type: ACTION.COMMIT_TEXT, settled }), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed, textKeys]);

  useEffect(() => {
    if (!enabled || loaded.current === query) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    setPage((p) => (p.status === STATUS.LOADING ? p : { ...p, status: STATUS.LOADING, error: "" }));
    loadRef.current({ ...query.filters, limit: query.limit, offset: query.offset }, signal)
      .then((data) => {
        if (signal.aborted) return;
        if (data === null) { setPage({ status: STATUS.NOT_CONNECTED, data: null, error: "" }); return; }
        const total = data.total || 0;
        if (total > 0 && query.offset >= total) {
          dispatch({ type: ACTION.OFFSET, offset: lastPageOffset(total, query.limit) });
          return;
        }
        loaded.current = query;
        setPage({ status: STATUS.READY, data, error: "" });
      })
      .catch((error) => {
        if (signal.aborted) return;
        if (isUnavailable(error)) setPage({ status: STATUS.NOT_CONNECTED, data: null, error: "" });
        else setPage({ status: STATUS.FAILED, data: null, error: (error && error.message) || "" });
      });
    return () => controller.abort();
  }, [enabled, query]);

  const setFilter = useCallback((key, value) => dispatch({ type: ACTION.SET, key, value, textKeys, resets }), [textKeys, resets]);
  const clearFilters = useCallback(() => dispatch({ type: ACTION.CLEAR, empty }), [empty]);
  const setOffset = useCallback((offset) => dispatch({ type: ACTION.OFFSET, offset }), []);
  const setLimit = useCallback((limit) => dispatch({ type: ACTION.LIMIT, limit }), []);
  const refresh = useCallback(() => dispatch({ type: ACTION.REFRESH }), []);

  return { ...page, filters, offset: query.offset, limit: query.limit, setFilter, clearFilters, setOffset, setLimit, refresh };
}
